import type { LoyaltyCard } from '@recipes/contracts';
import { Button, EmptyState } from '@recipes/ui';
import { CreditCard, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components/feedback/toaster';
import { removeLoyaltyCard } from '@/features/catalog/catalog-queue';
import { useCatalogBoot, useCatalogStores, useLoyaltyCards } from '@/features/catalog/hooks';
import {
  CardImage,
  LoyaltyCardDialog,
  LoyaltyCardViewDialog,
} from '@/features/catalog/loyalty-cards';
import { storeLabel } from '@/features/catalog/store-label';
import { ConfirmDialog } from '@/features/shared/confirm-dialog';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { toastError } from '@/lib/errors';
import { CatalogTabs, PendingNotice } from './catalog-pages';

/** The household's loyalty cards (user, 2026-09-25: Payback) – a tab next to the stores. */
export function CatalogCardsPage() {
  const { t } = useTranslation();
  const cards = useLoyaltyCards();
  const stores = useCatalogStores();
  const [editing, setEditing] = useState<LoyaltyCard | 'new' | null>(null);
  const [viewing, setViewing] = useState<LoyaltyCard | null>(null);
  const [deleting, setDeleting] = useState<LoyaltyCard | null>(null);
  const [removing, setRemoving] = useState(false);
  useCatalogBoot();

  const storeNames = (card: LoyaltyCard) =>
    stores
      .filter((store) => store.loyaltyCardId === card.id)
      .map((store) => storeLabel(store, { address: false }))
      .join(', ');

  const remove = async () => {
    if (!deleting) return;
    setRemoving(true);
    try {
      await removeLoyaltyCard(deleting.id);
      toast.success(t('catalog.cards.deleted'));
      setDeleting(null);
    } catch (error) {
      toastError(error);
    } finally {
      setRemoving(false);
    }
  };

  return (
    <PageContainer className="max-w-3xl">
      <FeaturePageHeader
        title={t('catalog.title')}
        description={t('catalog.cards.description')}
        actions={
          <Button onClick={() => setEditing('new')} data-testid="card-add">
            <Plus aria-hidden /> {t('catalog.cards.add')}
          </Button>
        }
      />
      <CatalogTabs />
      <PendingNotice />
      {cards.length === 0 ? (
        <EmptyState
          icon={<CreditCard aria-hidden />}
          title={t('catalog.cards.emptyTitle')}
          description={t('catalog.cards.emptyBody')}
        />
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2" data-testid="card-list">
          {cards.map((card) => (
            <li
              key={card.id}
              className="flex items-center gap-3 rounded-lg border p-3"
              data-testid="card-item"
            >
              <button
                type="button"
                className="shrink-0 rounded-md"
                aria-label={t('catalog.cards.show')}
                onClick={() => setViewing(card)}
                data-testid="card-show"
              >
                <CardImage card={card} className="h-14 w-20" />
              </button>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{card.name}</span>
                <span className="block truncate text-xs text-muted-foreground">
                  {storeNames(card) || t('catalog.cards.noStores')}
                </span>
              </span>
              {card.editable && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t('catalog.cards.edit')}
                    onClick={() => setEditing(card)}
                    data-testid="card-edit"
                  >
                    <Pencil aria-hidden />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t('common.delete')}
                    onClick={() => setDeleting(card)}
                    data-testid="card-delete"
                  >
                    <Trash2 aria-hidden />
                  </Button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      <LoyaltyCardDialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
        card={editing === 'new' ? null : editing}
      />
      {viewing && (
        <LoyaltyCardViewDialog
          card={viewing}
          open
          onOpenChange={(open) => {
            if (!open) setViewing(null);
          }}
        />
      )}
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        title={t('catalog.cards.deleteTitle', { name: deleting?.name ?? '' })}
        confirmLabel={t('common.delete')}
        destructive
        loading={removing}
        onConfirm={remove}
      />
    </PageContainer>
  );
}
